import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Clock, Gift, CheckCircle, Shield, Zap } from 'lucide-react';

const CTA = ({ onEnroll }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 7,
    hours: 12,
    minutes: 30,
    seconds: 45
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatNumber = (num) => String(num).padStart(2, '0');

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Limited Time Offer</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            Start Your Safety Training Today
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Join thousands of safety professionals protecting their workplaces
          </p>
        </div>

        {/* CTA Card */}
        <Card className="max-w-4xl mx-auto overflow-hidden shadow-2xl border-0">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 md:p-12">
            {/* Countdown timer */}
            <div className="text-center mb-8">
              <p className="text-slate-400 text-sm mb-4 flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Enrollment closes soon
              </p>
              <div className="flex justify-center gap-4">
                {[
                  { value: timeLeft.days, label: 'Days' },
                  { value: timeLeft.hours, label: 'Hours' },
                  { value: timeLeft.minutes, label: 'Minutes' },
                  { value: timeLeft.seconds, label: 'Seconds' }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-800 border border-slate-700 rounded-xl flex items-center justify-center mb-2">
                      <span className="text-2xl md:text-3xl font-bold text-white">
                        {formatNumber(item.value)}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Offer details */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mb-4">
                <Gift className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">100% FREE - No Credit Card Required</span>
              </div>
              
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="text-slate-500 line-through text-xl">$299</span>
                <span className="text-4xl md:text-5xl font-bold text-white">FREE</span>
              </div>

              <Button 
                onClick={onEnroll}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-7 text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-300 hover:shadow-emerald-500/40 hover:-translate-y-0.5 w-full md:w-auto"
              >
                <Zap className="w-5 h-5 mr-2" />
                Enroll Now for Free
              </Button>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-4 pt-8 border-t border-slate-700">
              {[
                { icon: CheckCircle, text: 'Certificate of completion' },
                { icon: Shield, text: 'Lifetime access' },
                { icon: Zap, text: 'Instant course access' }
              ].map((benefit, index) => (
                <div key={index} className="flex items-center justify-center gap-2 text-slate-400">
                  <benefit.icon className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default CTA;
